import React from 'react'
import {useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {equals, last} from 'ramda'

import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {
  getCancellationDeadline,
  getHasFreeCancellation
} from '@vio/core/src/offer/business/cancellationPenalties'
import {roomLabels} from '@vio/core/src/offer/business/getRoomLabels'

const RoomLabelsText = styled(Text)`
  color: ${cssTheme.colors.content.success.secondary};
`

const RoomLabelsItem = styled('span')<{isYourChoice?: boolean}>`
  display: inline-block;
  margin-right: ${cssTheme.layout.spacing.s200};
  &.--is-your-choice {
    ${cssTheme.typography.labelXS}
  }
`

const Dot = styled(RoomLabelsText)`
  display: inline-block;
  margin-left: ${cssTheme.layout.spacing.s200};
`

interface Props {
  /** Offer state */
  offer: SearchOffer
  shouldShowFreeCancellation?: boolean
}

export const YourChoiceAndFreeCancelationLabels = ({
  offer,
  shouldShowFreeCancellation = false
}: Props) => {
  const {formatMessage, formatDate} = useIntl()
  const hasFreeCancellation = getHasFreeCancellation(
    offer?.cancellationPenalties
  )

  const translateFreeCancellation = () => {
    if (hasFreeCancellation) {
      const freeCancellationDeadline = getCancellationDeadline(
        offer.cancellationPenalties
      )

      return formatMessage(
        {
          id: 'freeCancellationDeadline',
          defaultMessage: 'Free cancellation before {date}'
        },
        {
          date: formatDate(freeCancellationDeadline, {
            day: '2-digit',
            month: 'short',
            timeZone: 'UTC'
          })
        }
      )
    }

    return ''
  }
  const yourChoiceLabel = {
    id: roomLabels.yourSelection.id,
    label: formatMessage(roomLabels.yourSelection)
  }
  const translatedLabels = shouldShowFreeCancellation
    ? [
        yourChoiceLabel,
        {
          id: roomLabels.freeCancellation.id,
          label: translateFreeCancellation()
        }
      ]
    : [yourChoiceLabel]

  return (
    <RoomLabelsText
      variant="bodyXS"
      dataId="RoomLabels"
      className="--container-rooms-label"
    >
      {translatedLabels.map(({label, id}) => {
        return (
          <RoomLabelsItem
            className={cx(
              id === roomLabels.yourSelection.id && '--is-your-choice'
            )}
            key={id}
          >
            {label}
            {!equals(last(translatedLabels).label, label) && (
              <Dot variant="bodyXS" as="span">
                ·
              </Dot>
            )}
          </RoomLabelsItem>
        )
      })}
    </RoomLabelsText>
  )
}
